import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, X, Loader2, Building2, Upload, Image, Trophy, Crown, Shield, Star, ExternalLink, Edit, Check } from "lucide-react";

const SponsorsManager = () => {
  const { authAxios } = useAuth();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    logo_url: "",
    website_url: "",
    description: "",
    tier: "bronze",
    is_featured: false
  });

  const tiers = [
    { value: "platinum", label: "Platinum", icon: Crown, color: "text-cyan-400" },
    { value: "gold", label: "Gold", icon: Trophy, color: "text-yellow-400" },
    { value: "silver", label: "Silver", icon: Shield, color: "text-gray-400" },
    { value: "bronze", label: "Bronze", icon: Star, color: "text-amber-600" }
  ];

  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      const response = await axios.get(`${API}/sponsors`);
      setSponsors(response.data);
    } catch (e) {
      toast.error("Failed to fetch sponsors");
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e, isEdit = false) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('category', 'sponsors');

      const response = await authAxios.post('/upload', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const fullUrl = `${API.replace('/api', '')}${response.data.url}`;
      
      if (isEdit && editingSponsor) {
        setEditingSponsor({ ...editingSponsor, logo_url: fullUrl });
      } else {
        setFormData({ ...formData, logo_url: fullUrl });
      }
      toast.success("Logo uploaded!");
    } catch (e) {
      toast.error("Failed to upload logo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (editFileInputRef.current) editFileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: formData.name,
        logo_url: formData.logo_url || null,
        website_url: formData.website_url || null,
        description: formData.description || null,
        tier: formData.tier,
        is_featured: formData.is_featured
      };
      
      await authAxios.post('/sponsors', payload);
      toast.success("Sponsor added successfully!");
      setShowForm(false);
      resetForm();
      fetchSponsors();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add sponsor");
    }
  };

  const handleEdit = (sponsor) => {
    setEditingSponsor({ ...sponsor });
    setShowForm(false);
  };

  const handleSaveEdit = async () => {
    if (!editingSponsor) return;
    
    try {
      const payload = {
        name: editingSponsor.name,
        logo_url: editingSponsor.logo_url || null,
        website_url: editingSponsor.website_url || null,
        description: editingSponsor.description || null,
        tier: editingSponsor.tier,
        is_featured: editingSponsor.is_featured
      };
      
      await authAxios.put(`/sponsors/${editingSponsor.id}`, payload);
      toast.success("Sponsor updated!");
      setEditingSponsor(null);
      fetchSponsors();
    } catch (e) {
      toast.error("Failed to update sponsor");
    }
  };

  const handleCancelEdit = () => {
    setEditingSponsor(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this sponsor?")) return;
    
    try {
      await authAxios.delete(`/sponsors/${id}`);
      toast.success("Sponsor deleted");
      if (editingSponsor?.id === id) setEditingSponsor(null);
      fetchSponsors();
    } catch (e) {
      toast.error("Failed to delete sponsor");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      logo_url: "",
      website_url: "",
      description: "",
      tier: "bronze",
      is_featured: false
    });
  };

  const getTierIcon = (tier) => {
    const tierConfig = tiers.find(t => t.value === tier);
    if (!tierConfig) return Star;
    return tierConfig.icon;
  };

  const getTierColor = (tier) => {
    const tierConfig = tiers.find(t => t.value === tier);
    if (!tierConfig) return "text-amber-600";
    return tierConfig.color;
  };

  const getTierBorder = (tier) => {
    switch (tier) {
      case 'platinum': return 'border-cyan-400';
      case 'gold': return 'border-yellow-400';
      case 'silver': return 'border-gray-400';
      default: return 'border-amber-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
          <Building2 className="w-5 h-5 text-cyan-400" />
          Sponsors ({sponsors.length})
        </h2>
        <button
          onClick={() => { setShowForm(!showForm); setEditingSponsor(null); }}
          data-testid="add-sponsor-btn"
          className="cyber-btn py-2 px-4 text-xs border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black"
        >
          <span className="flex items-center gap-2">
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? "CANCEL" : "ADD SPONSOR"}
          </span>
        </button>
      </div>

      {/* Add Form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="cyber-card p-6 space-y-4 border-cyan-400/30" data-testid="sponsor-form">
          <div className="flex items-center gap-2 mb-4 pb-2 border-b border-cyan-400/30">
            <Plus className="w-5 h-5 text-cyan-400" />
            <span className="font-orbitron text-sm text-cyan-400">ADD NEW SPONSOR</span>
          </div>
          
          {/* Logo Upload Section */}
          <div className="border border-dashed border-cyan-400/50 p-4">
            <Label className="font-orbitron text-sm text-cyan-400 mb-3 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              COMPANY LOGO
            </Label>
            
            <div className="flex items-start gap-4">
              <div className="w-32 h-32 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                {formData.logo_url ? (
                  <img src={formData.logo_url} alt="Logo Preview" className="w-full h-full object-contain p-2" />
                ) : (
                  <Building2 className="w-10 h-10 text-gray-600" />
                )}
              </div>

              <div className="flex-1 space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleLogoUpload(e, false)}
                  className="hidden"
                  id="sponsor-logo-upload"
                />
                <label
                  htmlFor="sponsor-logo-upload"
                  data-testid="sponsor-upload-btn"
                  className={`cyber-btn py-3 px-4 text-xs cursor-pointer inline-flex items-center gap-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      UPLOADING...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      UPLOAD LOGO
                    </>
                  )}
                </label>
                <p className="font-rajdhani text-xs text-gray-500">PNG with transparent background recommended</p>
                <Input
                  value={formData.logo_url}
                  onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                  placeholder="Or paste logo URL..."
                  className="bg-void-black border-white/10 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Sponsor Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Company Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                placeholder="Nike, Gatorade, etc."
                className="bg-void-black border-white/10"
                data-testid="sponsor-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Website URL</Label>
              <Input
                value={formData.website_url}
                onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                placeholder="https://company.com"
                className="bg-void-black border-white/10"
              />
            </div>
          </div>

          {/* Tier Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="font-orbitron text-xs text-gray-400">Sponsor Tier *</Label>
              <Select value={formData.tier} onValueChange={(v) => setFormData({...formData, tier: v})}>
                <SelectTrigger className="bg-void-black border-white/10" data-testid="sponsor-tier-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-void-black border-white/10">
                  {tiers.map(tier => {
                    const TierIcon = tier.icon;
                    return (
                      <SelectItem key={tier.value} value={tier.value}>
                        <div className="flex items-center gap-2">
                          <TierIcon className={`w-4 h-4 ${tier.color}`} />
                          <span>{tier.label}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Switch
                checked={formData.is_featured}
                onCheckedChange={(v) => setFormData({...formData, is_featured: v})}
              />
              <div>
                <Label className="font-orbitron text-sm text-neon-pink">FEATURED SPONSOR</Label>
                <p className="font-rajdhani text-xs text-gray-500">Display prominently</p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label className="font-orbitron text-xs text-gray-400">Description (Optional)</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Brief description..."
              rows={2}
              className="bg-void-black border-white/10 font-rajdhani"
            />
          </div>

          {/* Submit */}
          <div className="flex justify-end">
            <button type="submit" className="cyber-btn py-2 px-6 text-xs border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black" data-testid="save-sponsor-btn">
              <span className="flex items-center gap-2">
                <Save className="w-4 h-4" />
                SAVE SPONSOR
              </span>
            </button>
          </div>
        </form>
      )}

      {/* Sponsors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {sponsors.map(sponsor => {
          const TierIcon = getTierIcon(sponsor.tier);
          const isEditing = editingSponsor?.id === sponsor.id;
          
          return (
            <div 
              key={sponsor.id} 
              className={`cyber-card overflow-hidden border-2 ${getTierBorder(sponsor.tier)} ${sponsor.is_featured ? 'ring-2 ring-neon-pink' : ''} ${isEditing ? 'ring-2 ring-neon-green' : ''}`} 
              data-testid={`sponsor-item-${sponsor.id}`}
            >
              {isEditing ? (
                /* Edit Mode */
                <div className="p-4 space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b border-neon-green/30">
                    <Edit className="w-4 h-4 text-neon-green" />
                    <span className="font-orbitron text-xs text-neon-green">EDITING SPONSOR</span>
                  </div>

                  {/* Edit Logo */}
                  <div className="flex items-center gap-4">
                    <div className="w-20 h-20 bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {editingSponsor.logo_url ? (
                        <img src={editingSponsor.logo_url} alt="Logo" className="w-full h-full object-contain p-1" />
                      ) : (
                        <Building2 className="w-8 h-8 text-gray-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <input
                        ref={editFileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleLogoUpload(e, true)}
                        className="hidden"
                        id={`edit-sponsor-logo-${sponsor.id}`}
                      />
                      <label
                        htmlFor={`edit-sponsor-logo-${sponsor.id}`}
                        data-testid={`edit-logo-btn-${sponsor.id}`}
                        className={`cyber-btn py-2 px-3 text-xs cursor-pointer inline-flex items-center gap-2 border-neon-green text-neon-green hover:bg-neon-green hover:text-black ${uploading ? 'opacity-50' : ''}`}
                      >
                        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        {uploading ? 'UPLOADING...' : 'CHANGE LOGO'}
                      </label>
                      <Input
                        value={editingSponsor.logo_url || ''}
                        onChange={(e) => setEditingSponsor({...editingSponsor, logo_url: e.target.value})}
                        placeholder="Or paste URL..."
                        className="bg-void-black border-white/10 text-xs mt-2"
                      />
                    </div>
                  </div>

                  {/* Edit Fields */}
                  <div className="space-y-3">
                    <div>
                      <Label className="font-orbitron text-[10px] text-gray-500">Company Name</Label>
                      <Input
                        value={editingSponsor.name}
                        onChange={(e) => setEditingSponsor({...editingSponsor, name: e.target.value})}
                        className="bg-void-black border-white/10 text-sm h-8"
                      />
                    </div>
                    <div>
                      <Label className="font-orbitron text-[10px] text-gray-500">Website URL</Label>
                      <Input
                        value={editingSponsor.website_url || ''}
                        onChange={(e) => setEditingSponsor({...editingSponsor, website_url: e.target.value})}
                        placeholder="https://..."
                        className="bg-void-black border-white/10 text-sm h-8"
                      />
                    </div>
                    <div>
                      <Label className="font-orbitron text-[10px] text-gray-500">Tier</Label>
                      <Select value={editingSponsor.tier} onValueChange={(v) => setEditingSponsor({...editingSponsor, tier: v})}>
                        <SelectTrigger className="bg-void-black border-white/10 h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-void-black border-white/10">
                          {tiers.map(tier => {
                            const Icon = tier.icon;
                            return (
                              <SelectItem key={tier.value} value={tier.value}>
                                <div className="flex items-center gap-2">
                                  <Icon className={`w-3 h-3 ${tier.color}`} />
                                  <span>{tier.label}</span>
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="font-orbitron text-[10px] text-gray-500">Description</Label>
                      <Textarea
                        value={editingSponsor.description || ''}
                        onChange={(e) => setEditingSponsor({...editingSponsor, description: e.target.value})}
                        rows={2}
                        className="bg-void-black border-white/10 text-xs"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={editingSponsor.is_featured}
                        onCheckedChange={(v) => setEditingSponsor({...editingSponsor, is_featured: v})}
                      />
                      <Label className="text-xs text-neon-pink">Featured</Label>
                    </div>
                  </div>

                  {/* Edit Actions */}
                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <button
                      onClick={handleCancelEdit}
                      className="text-gray-500 hover:text-white text-xs flex items-center gap-1"
                    >
                      <X className="w-4 h-4" /> Cancel
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      className="cyber-btn py-2 px-4 text-xs border-neon-green text-neon-green hover:bg-neon-green hover:text-black"
                      data-testid={`save-edit-${sponsor.id}`}
                    >
                      <Check className="w-4 h-4 mr-1 inline" /> SAVE
                    </button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <>
                  {sponsor.is_featured && (
                    <div className="bg-neon-pink px-2 py-1 text-center">
                      <span className="font-orbitron text-[10px] text-white">★ MVP SPONSOR ★</span>
                    </div>
                  )}
                  
                  <div className="aspect-square bg-white/5 p-4 flex items-center justify-center">
                    {sponsor.logo_url ? (
                      <img src={sponsor.logo_url} alt={sponsor.name} className="w-full h-full object-contain" />
                    ) : (
                      <Building2 className="w-16 h-16 text-gray-700" />
                    )}
                  </div>
                  
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <TierIcon className={`w-4 h-4 ${getTierColor(sponsor.tier)}`} />
                          <span className={`font-orbitron text-[10px] uppercase ${getTierColor(sponsor.tier)}`}>
                            {sponsor.tier}
                          </span>
                        </div>
                        <h3 className="font-orbitron text-sm text-white truncate">{sponsor.name}</h3>
                        {sponsor.website_url && (
                          <a 
                            href={sponsor.website_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="font-rajdhani text-xs text-neon-blue hover:underline flex items-center gap-1 mt-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Website
                          </a>
                        )}
                      </div>
                    </div>
                    
                    {sponsor.description && (
                      <p className="font-rajdhani text-xs text-gray-500 mt-2 line-clamp-2">{sponsor.description}</p>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-2 mt-3 pt-3 border-t border-white/5">
                      <button
                        onClick={() => handleEdit(sponsor)}
                        className="text-gray-500 hover:text-neon-green transition-colors p-2 hover:bg-neon-green/10 rounded"
                        title="Edit sponsor"
                        data-testid={`edit-sponsor-${sponsor.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(sponsor.id)}
                        className="text-gray-500 hover:text-red-500 transition-colors p-2 hover:bg-red-500/10 rounded"
                        title="Delete sponsor"
                        data-testid={`delete-sponsor-${sponsor.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {sponsors.length === 0 && (
        <div className="text-center py-12 cyber-card" data-testid="empty-sponsors-state">
          <Building2 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="font-rajdhani text-gray-500">No sponsors yet. Add your first sponsor!</p>
        </div>
      )}
    </div>
  );
};

export default SponsorsManager;
